"use client"

import * as React from "react"

import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import { useFinancePeriod } from "@/features/finance/hooks/use-finance-period"
import { FinanceContext, type FinancePageContext } from "@/features/finance/model/finance-tabs"
import { OverviewContext } from "@ibada/shared-types/overview"
import { PeriodPreset } from "@/shared/lib/dates/period-range"
import { useBranchCurrency } from "@/features/finance/hooks/use-branch-currency"
import { resolveIdentityBranchId } from "@/features/finance/overview/lib/identity-branch"

/**
 * Everything a Finance Overview widget needs to scope its own query, resolved once and shared. Splitting
 * scope (current-state, period-independent) from period (the shared selector) is deliberate: a widget that
 * reads only {@link FinanceOverviewScope} keeps its query key free of the period, so changing the period
 * selector does NOT refetch current-balance widgets — only period-sensitive widgets rebuild their key.
 */
/** The one place a finance page context becomes an API overview context. */
const OVERVIEW_CONTEXT_BY_FINANCE_CONTEXT: Record<FinancePageContext, OverviewContext> = {
  [FinanceContext.Branch]: OverviewContext.Branch,
  [FinanceContext.Parish]: OverviewContext.Parish,
  [FinanceContext.Global]: OverviewContext.Global,
}

export interface FinanceOverviewScope {
  workspaceId: string | null
  /** branch, parish or global; parish resolves to the real system branch id below. */
  context: FinancePageContext
  /**
   * The same scope as an API `OverviewContext`, mapped ONCE.
   *
   * Every widget used to re-derive this with its own `context === "parish" ? Parish : Branch` ternary, so a
   * third member meant finding and fixing four identical ternaries — and any one of them missed would quietly
   * request branch data on the consolidated board.
   */
  overviewContext: OverviewContext
  /** Scoped branch id — the system (parish) branch id in parish context; null for global (a set of branches). */
  branchId: string | null
  /**
   * A branch id to ask WORKSPACE-level questions with — governance and disclosure (ADR-0024).
   *
   * Global deliberately has no `branchId`, which silently disabled the finance-setup query on the
   * consolidated board: it lost the governance banner, and the board kept every money widget and rendered
   * them as $0.00 once the server started withholding. The facts wanted here are org-wide, so ANY branch of
   * the workspace answers them — this is simply a branch that exists, never a claim about scope.
   */
  identityBranchId: string | null
  /** Active Finance currency code (never hardcoded downstream). */
  currency: string
  canView: boolean
  canEdit: boolean
}

export interface FinanceOverviewPeriod {
  preset: PeriodPreset
  setPreset: (preset: PeriodPreset) => void
  /** `YYYY-MM-DD` in the branch timezone (undefined only if a fiscal preset can't resolve). */
  from?: string
  to?: string
  hasFiscalPeriods: boolean
}

interface FinanceOverviewContextValue {
  scope: FinanceOverviewScope
  period: FinanceOverviewPeriod
}

const FinanceOverviewContext = React.createContext<FinanceOverviewContextValue | null>(null)

export function FinanceOverviewProvider({
  children,
  branchIdOverride,
  contextOverride,
}: {
  children: React.ReactNode
  /** Branch Details console: scope the finance overview to a specific inspected branch (read-only). */
  branchIdOverride?: string
  /**
   * Force a context the URL cannot express.
   *
   * `activeContext` is derived from the branch slug, so it can only ever say branch or parish. The Global
   * Finance section is a different SCOPE of the same workspace rather than a different branch, so it declares
   * itself here. Global carries no `branchId` at all — its scope is the set of every branch, resolved server
   * side, which is the whole reason it needs its own backend contract (ADR-0011).
   */
  contextOverride?: FinanceContext
}) {
  const {
    workspaceId,
    activeContext,
    scopeBranch,
    parishBranch,
    canViewBranchFinance,
    canEditBranchFinance,
    canViewParishFinance,
    canEditParishFinance,
  } = useFinancePageState()

  const currency = useBranchCurrency()
  const period = useFinancePeriod({ defaultPreset: PeriodPreset.MonthToDate })

  // Inspecting a specific branch (Branch Details) forces branch context on the inspected id and opens a
  // read-only view — the workspace read-oversight ability authorizes the finance reads.
  const inspecting = Boolean(branchIdOverride)
  const isGlobal = !inspecting && contextOverride === FinanceContext.Global
  const isParish = !inspecting && !isGlobal && activeContext === FinanceContext.Parish
  /*
   * A mono workspace resolves its operational branch, so `isParish` is already false there and the branch grant
   * answers — no structure test needed. `inspecting` and `isGlobal` still decide first, which is why this board
   * checks the grants itself instead of taking the hook's resolved flag.
   */
  const parishGrantApplies = isParish
  const branchId = isGlobal
    ? null
    : (branchIdOverride ?? (isParish ? parishBranch?.id ?? scopeBranch?.id ?? null : scopeBranch?.id ?? null))
  // Never null just because the scope is consolidated — see `identityBranchId`.
  const identityBranchId = resolveIdentityBranchId(branchId, {
    parishBranchId: parishBranch?.id,
    scopeBranchId: scopeBranch?.id,
  })
  const context: FinancePageContext = inspecting
    ? FinanceContext.Branch
    : (contextOverride ?? activeContext)

  const scope = React.useMemo<FinanceOverviewScope>(
    () => ({
      workspaceId: workspaceId ?? null,
      context,
      overviewContext: OVERVIEW_CONTEXT_BY_FINANCE_CONTEXT[context],
      branchId,
      identityBranchId,
      currency,
      // Global is gated on PARISH finance access — the same grant the backend checks — and is read-only:
      // there is no single branch to post into from a consolidated board.
      canView:
        inspecting ? true : isGlobal || parishGrantApplies ? canViewParishFinance : canViewBranchFinance,
      canEdit: inspecting || isGlobal ? false : parishGrantApplies ? canEditParishFinance : canEditBranchFinance,
    }),
    [
      workspaceId,
      context,
      branchId,
      identityBranchId,
      currency,
      inspecting,
      isGlobal,
      parishGrantApplies,
      canViewBranchFinance,
      canEditBranchFinance,
      canViewParishFinance,
      canEditParishFinance,
    ],
  )

  const value = React.useMemo<FinanceOverviewContextValue>(
    () => ({
      scope,
      period: {
        preset: period.preset,
        setPreset: period.setPreset,
        from: period.from,
        to: period.to,
        hasFiscalPeriods: period.hasFiscalPeriods,
      },
    }),
    [scope, period.preset, period.setPreset, period.from, period.to, period.hasFiscalPeriods],
  )

  return <FinanceOverviewContext.Provider value={value}>{children}</FinanceOverviewContext.Provider>
}

function useFinanceOverviewContext(): FinanceOverviewContextValue {
  const ctx = React.useContext(FinanceOverviewContext)
  if (!ctx) {
    throw new Error("Finance Overview widgets must be rendered inside a <FinanceOverviewProvider>")
  }
  return ctx
}

/** Current-state scope (period-independent). Use for widgets whose keys must NOT include the period. */
export function useFinanceOverviewScope(): FinanceOverviewScope {
  return useFinanceOverviewContext().scope
}

/** The shared period selector state. Use only in period-sensitive widgets. */
export function useFinanceOverviewPeriod(): FinanceOverviewPeriod {
  return useFinanceOverviewContext().period
}
